import {
  queryOptions,
  useMutation,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';
import * as React from 'react';
import type {
  CvUpdateTask,
  Filter as CvUpdateTaskFilter,
} from 'schema/cv_update_task';
import type { LocString } from 'schema/schema';
import {
  invalidateMasterdataCount,
  invalidateUnapprovedCount,
} from 'stores/Counts';
import { invalidateNumberOfUsers } from 'stores/NumberOfUsers';
import { invalidateNumberOfUsers as invalidateNumberOfUsersForRefCustomer } from 'stores/NumberOfUsersForRefCustomer';
import { invalidateUnapproved } from 'stores/Unapproved';
import type { StoreMutation } from 'stores/types';
import { http } from 'util/fetch';
import { invariant } from 'util/invariant';
import { isNotEmpty } from 'util/value';
import { invalidateAllCustomerDescriptions } from './Customers';
import { invalidateAllProjectDescriptions } from './ProjectDescriptions';
import { invalidateAllProjectFieldsOverviews } from './ProjectFieldOverview';

const queries = {
  base: ['cv-update-task'] as const,
  fetch: (taskId: string) =>
    queryOptions({
      queryKey: [...queries.base, taskId] as const,
      queryFn: async ({ signal }) => {
        const cvUpdateTask = await http.get<CvUpdateTask>(
          `/cv-update-tasks/${taskId}`,
          { signal },
        );

        if (cvUpdateTask.state === 'completed') {
          void Promise.allSettled([
            reload_number_of_users_for(cvUpdateTask),
            invalidate_project_experience_deviations(cvUpdateTask),
            invalidateMasterdataCount(
              cvUpdateTask.section_type,
              cvUpdateTask.field_name,
            ),
            notify_unapproved_for(cvUpdateTask),
          ]);
        }

        return cvUpdateTask;
      },
    }),
};

async function reload_number_of_users_for(task: CvUpdateTask) {
  await Promise.allSettled(
    Object.entries(task.new_field_values || {})
      .filter(([lang, value]) => value !== task.original_field_values[lang])
      .map(() => {
        if (task.field_name === 'description') {
          // We don't have the customer id that was renamed so invalidate all
          return invalidateNumberOfUsersForRefCustomer();
        }

        return invalidateNumberOfUsers({
          sectionType: task.section_type,
          fieldName: task.field_name,
        });
      }),
  );
}

async function invalidate_project_experience_deviations(task: CvUpdateTask) {
  if (task.section_type !== 'project_experiences') {
    return;
  }

  if (task.field_name === 'description') {
    // Reference project rename complete so invalidate project deviations stores
    await Promise.allSettled([
      invalidateAllProjectDescriptions(),
      invalidateAllProjectFieldsOverviews(),
    ]);
  } else if (task.field_name === 'customer') {
    // Reference customer rename complete so invalidate customer deviations stores
    await invalidateAllCustomerDescriptions();
  }
}

async function notify_unapproved_for(task: CvUpdateTask) {
  await Promise.allSettled([
    invalidateUnapproved(task.section_type, task.field_name),
    ...Object.keys(task.new_field_values || {}).map((lang) =>
      invalidateUnapprovedCount(task.section_type, task.field_name, lang),
    ),
  ]);
}

interface CreateCvUpdateTaskVariables {
  section_type: string;
  field_name: string;
  original_values: LocString | null;
  new_values: LocString | null;
  original_category_values: LocString | null;
  new_category_values: LocString | null;
  cv_filters?: CvUpdateTaskFilter;
}

export type CreateCvUpdateTaskMutation = StoreMutation<
  CvUpdateTask,
  CreateCvUpdateTaskVariables
>;

type UpdateTasksContextType = {
  updateTasks: readonly string[];
  create: CreateCvUpdateTaskMutation;
  addTaskForPolling: (taskId: string) => void;
  clearAll: () => void;
};

const UpdateTasksContext = React.createContext<UpdateTasksContextType | null>(
  null,
);

export const UpdateTasksProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();

  const [updateTasks, setUpdateTasks] = React.useState<
    UpdateTasksContextType['updateTasks']
  >([]);

  const clearAll = React.useCallback(() => {
    setUpdateTasks((updateTasks) => {
      if (updateTasks.length === 0) {
        return updateTasks;
      }

      return [];
    });

    queryClient.removeQueries({ queryKey: queries.base });
  }, [queryClient]);

  const { mutate: create } = useMutation({
    mutationFn: (cv_update_task: CreateCvUpdateTaskVariables) => {
      const url = `/cv-update-tasks`;
      const body = { cv_update_task };

      return http.post<CvUpdateTask>(url, { body });
    },
    onMutate: () => {
      clearAll();
    },
    onSuccess: (data, _vars, _onMutateResult, { client }) => {
      client.setQueryData(queries.fetch(data.id).queryKey, data);

      setUpdateTasks((updateTasks) => [...updateTasks, data.id]);
    },
  });

  const value = React.useMemo<UpdateTasksContextType>(() => {
    function addTaskForPolling(taskId: string) {
      setUpdateTasks((updateTasks) => {
        if (updateTasks.includes(taskId)) {
          return updateTasks;
        }

        return [...updateTasks, taskId];
      });
    }

    return { updateTasks, create, addTaskForPolling, clearAll };
  }, [clearAll, create, updateTasks]);

  return (
    <UpdateTasksContext.Provider value={value}>
      {children}
    </UpdateTasksContext.Provider>
  );
};

function useUpdateTasksContext() {
  const context = React.useContext(UpdateTasksContext);

  invariant(context, 'useUpdateTasksContext must be used within the context');

  return context;
}

export function useCreate() {
  const { create } = useUpdateTasksContext();

  return create;
}

export function useAddTaskForPolling() {
  const { addTaskForPolling } = useUpdateTasksContext();

  return addTaskForPolling;
}

export function useClearAll() {
  const { clearAll } = useUpdateTasksContext();

  return clearAll;
}

export type RenameFunction = (
  section_type: string,
  field_type: string,
  language_code: string,
  original_value: string,
  new_value: string,
  filters?: CvUpdateTaskFilter,
) => void;

export function useRename(): RenameFunction {
  const { create } = useUpdateTasksContext();

  return (
    section_type,
    field_type,
    language_code,
    original_value,
    new_value,
    filters,
  ) => {
    if (!original_value || !new_value || original_value === new_value) {
      return;
    }

    create({
      section_type,
      field_name: field_type,
      original_values: { [language_code]: original_value },
      new_values: { [language_code]: new_value },
      original_category_values: null,
      new_category_values: null,
      cv_filters: filters,
    });
  };
}

export function useCvUpdateTasks() {
  const { updateTasks } = useUpdateTasksContext();

  return useQueries({
    queries: updateTasks.map((taskId) =>
      queryOptions({
        ...queries.fetch(taskId),
        refetchInterval: (query) => {
          const { data } = query.state;

          if (data?.state === 'completed' || data?.state === 'failed') {
            return false;
          }

          return 1000;
        },
        gcTime: Infinity,
        staleTime: 60_000,
      }),
    ),
    combine: (results) =>
      results.map((result) => result.data).filter(isNotEmpty),
  });
}
